// Sign-in with Cognito managed login: authorization code + PKCE, no client
// secret. Tokens stay in this tab's sessionStorage, so a reload keeps the
// session until the refresh token runs out (12 hours); closing the tab ends it.
// The access token is renewed silently with the refresh token before it expires.
//
// With Sign in with Xero, the web client goes straight to Xero (identity_provider=Xero), skipping
// Cognito's page. /?signin=admin uses the break-glass client instead: Cognito's own sign-in with
// password and TOTP, for when Xero or our Xero connection is down. The choice lasts for the tab.

import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';

export interface Auth {
  /** Current access token, or null in mock mode. */
  token(): Promise<string | null>;
  /** After a 401: try a silent refresh once; false means the user must sign in again. */
  renew(): Promise<boolean>;
  signOut(): void;
  /** Who is signed in, from the ID token (the access token has no email). Nulls in mock mode. */
  profile(): { email: string | null; username: string | null };
}

export const mockAuth: Auth = {
  token: async () => null,
  renew: async () => false,
  signOut: () => {
    sessionStorage.removeItem('mockRole');
    location.href = '/';
  },
  profile: () => ({ email: null, username: null }),
};

export interface SignInOptions {
  identityProvider?: string;
  adminClientId?: string;
}

const SIGNIN_MODE = 'signinMode';
/** Set by sign-out so the next load shows "You're signed out" instead of signing straight back in (Xero would). */
const SIGNED_OUT = 'signedOut';

export function clearSignedOut(): void {
  sessionStorage.removeItem(SIGNED_OUT);
}

/** Cognito puts a pre-token trigger's refusal in error_description after its own prefix. */
export function signInError(description: string | null, code: string | null): string {
  const text = (description ?? code ?? 'unknown error').replace(/^PreTokenGeneration failed with error\s*/i, '').trim();
  return `Sign-in failed: ${text}`;
}

export async function cognitoAuth(cognitoDomain: string, webClientId: string, options: SignInOptions = {}): Promise<Auth | null | 'signed-out'> {
  const domain = cognitoDomain.replace(/\/$/, '');
  const origin = location.origin;
  const params = new URLSearchParams(location.search);
  const signedOut = sessionStorage.getItem(SIGNED_OUT);
  if (signedOut && !params.has('code')) {
    // Back from Cognito's logout: end the Xero session too, or Xero would sign straight back in.
    // Xero returns here afterwards (the site's root URL is registered on the Xero app).
    if (signedOut === 'xero') {
      sessionStorage.setItem(SIGNED_OUT, 'done');
      location.href = `https://login.xero.com/identity/connect/endsession?post_logout_redirect_uri=${encodeURIComponent(`${origin}/`)}`;
      return null;
    }
    return 'signed-out';
  }
  if (params.get('signin') === 'admin' && options.adminClientId) sessionStorage.setItem(SIGNIN_MODE, 'admin');
  const admin = Boolean(options.adminClientId) && sessionStorage.getItem(SIGNIN_MODE) === 'admin';
  const clientId = admin ? options.adminClientId! : webClientId;
  const extraQueryParams = !admin && options.identityProvider ? { identity_provider: options.identityProvider } : undefined;
  const manager = new UserManager({
    authority: domain,
    client_id: clientId,
    redirect_uri: `${origin}/`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: 'code',
    scope: 'openid email profile',
    metadata: {
      issuer: domain,
      authorization_endpoint: `${domain}/oauth2/authorize`,
      token_endpoint: `${domain}/oauth2/token`,
      userinfo_endpoint: `${domain}/oauth2/userInfo`,
      revocation_endpoint: `${domain}/oauth2/revoke`,
      end_session_endpoint: `${domain}/logout`,
    },
    loadUserInfo: false,
    extraQueryParams,
    automaticSilentRenew: true,
    // Tokens live in this tab's sessionStorage, so a reload keeps the session (up to the 12-hour refresh token)
    // and closing the tab ends it. Not localStorage: it would outlive the browser. The CSP allows no inline script.
    // The PKCE verifier needs to survive the redirect too, so state uses sessionStorage.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });

  let user: User | null = null;
  if (params.has('code') && params.has('state')) {
    user = await manager.signinRedirectCallback();
    // Back to where sign-in started: the screen in the hash, or the Plaid OAuth return page with its query (Plaid's oauth_state_id).
    const state = typeof user.state === 'string' ? user.state : '';
    const back = /^\/plaid-return(\?[^#]*)?$/.test(state) || /^\/(#\/?[\w-]+(\/[\w-]+)*)?$/.test(state) ? state : '/';
    history.replaceState(null, '', back);
  } else if (params.has('error')) {
    sessionStorage.removeItem(SIGNIN_MODE);
    throw new Error(signInError(params.get('error_description'), params.get('error')));
  } else {
    user = await manager.getUser();
    // Back after the access token lapsed: renew with the refresh token before falling back to the login page.
    if (user?.expired && user.refresh_token) user = await manager.signinSilent().catch(() => null);
  }
  if (!user || user.expired) {
    // No session in this tab. On /plaid-return the query carries Plaid's OAuth state, so it comes along.
    await manager.signinRedirect({ state: location.pathname === '/plaid-return' ? location.pathname + location.search : location.pathname + location.hash });
    return null; // the browser is leaving
  }
  manager.events.addUserLoaded((u) => {
    user = u;
  });
  manager.events.addUserUnloaded(() => {
    user = null;
  });

  return {
    async token() {
      if (!user || user.expired) {
        try {
          user = await manager.signinSilent();
        } catch {
          user = null;
        }
      }
      if (!user) {
        await manager.signinRedirect();
        return null;
      }
      return user.access_token;
    },
    async renew() {
      try {
        user = await manager.signinSilent();
        return Boolean(user);
      } catch {
        return false;
      }
    },
    profile() {
      const p = user?.profile;
      const email = typeof p?.email === 'string' && p.email ? p.email : null;
      const name = p?.['cognito:username'] ?? p?.preferred_username;
      return { email, username: typeof name === 'string' && name ? name : null };
    },
    signOut() {
      // Revoke the refresh token and clear this tab's stored session before leaving for Cognito's logout.
      // A real sign-out ends Xero's session too: Cognito's logout returns here, the app sends the
      // browser to Xero's end-session endpoint, and Xero returns to the signed-out page.
      sessionStorage.removeItem(SIGNIN_MODE);
      sessionStorage.setItem(SIGNED_OUT, admin || !options.identityProvider ? 'done' : 'xero');
      const leave = () => {
        // Cognito's logout endpoint takes client_id and logout_uri, not the OIDC id_token_hint form.
        location.href = `${domain}/logout?client_id=${encodeURIComponent(clientId)}&logout_uri=${encodeURIComponent(`${origin}/`)}`;
      };
      manager
        .revokeTokens(['refresh_token'])
        .catch(() => undefined)
        .then(() => manager.removeUser())
        .finally(leave);
    },
  };
}
